import { AudioPlayTask, AudioTaskState } from '../audio-play-task';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class AudioPlaybackTaskFactory {
    private _audio = new Audio();

    public createAudioPlaybackTask(
        source: { audioBytes: Uint8Array } | { url: string },
    ): AudioPlayTask {
        const stateSubject = new BehaviorSubject<AudioTaskState>(
            'unknown',
        );

        const events = ['play', 'playing', 'pause', 'timeupdate', 'ended', 'loadedmetadata'];
        const cleanupQueue: (() => void)[] = [];

        const finalizeTask = () => {
            cleanupQueue.forEach((item) => item());
            stateSubject.next('complete');
            stateSubject.complete();
        };

        const eventsListener = (event: string) => {
            if (event === 'play') {
                stateSubject.next('playing');
            } else if (event === 'pause') {
                stateSubject.next('paused');
            } else if (event === 'ended') {
                finalizeTask();
            }
        };

        events.forEach((event) => {
            const particularListener = () => eventsListener(event);

            this._audio.addEventListener(event, particularListener);
            cleanupQueue.push(() => this._audio.removeEventListener(event, particularListener));
        });

        const composeAudioUrl = async () => {
            if ('url' in source) {
                return source.url;
            }

            const blob = new Blob([source.audioBytes as Uint8Array<ArrayBuffer>], {
                type: 'audio/wav',
            });
            const blobWithHeader = await this.appendWavHeader(blob);
            return URL.createObjectURL(blobWithHeader);
        };

        return {
            state: stateSubject.asObservable(),
            init: async () => {
                if (!this._audio.paused) {
                    this._audio.pause();
                }

                this._audio.src = await composeAudioUrl();
                await this._audio.play();
            },
            pause: () => {
                this._audio.pause();
                return Promise.resolve();
            },
            resume: () => this._audio.play(),
            stop: () => {
                this._audio.pause();
                finalizeTask();
                return Promise.resolve();
            },
        };
    }

    private async appendWavHeader(rawPcmBlob: Blob): Promise<Blob> {
        const pcmArrayBuffer = await rawPcmBlob.arrayBuffer();
        const wavHeader = this.getWavHeader(pcmArrayBuffer.byteLength);
        const combinedBuffer = new Uint8Array(wavHeader.byteLength + pcmArrayBuffer.byteLength);
        combinedBuffer.set(new Uint8Array(wavHeader), 0);
        combinedBuffer.set(new Uint8Array(pcmArrayBuffer), wavHeader.byteLength);
        return new Blob([combinedBuffer], { type: 'audio/wav' });
    }

    private getWavHeader(pcmDataLength: number): ArrayBuffer {
        const wavHeader = new ArrayBuffer(44);
        const view = new DataView(wavHeader);
        const sampleRate = 24000;
        const numChannels = 1; // Mono
        const bitsPerSample = 16; // PCM16
        const blockAlign = (numChannels * bitsPerSample) / 8;
        const byteRate = sampleRate * blockAlign;
        const dataSize = pcmDataLength;
        const chunkSize = 36 + dataSize;
        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, chunkSize, true);
        this.writeString(view, 8, 'WAVE');
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
        view.setUint16(20, 1, true); // AudioFormat (1 = PCM)
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitsPerSample, true);
        this.writeString(view, 36, 'data');
        view.setUint32(40, dataSize, true);
        return wavHeader;
    }

    private writeString(view: DataView, offset: number, string: string) {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }
}
